'use client'

/**
 * Google One-Tap browser glue (ui-react `OneTapSdkLoader`/`GoogleOneTapService` parity).
 * Loads the GSI client, initializes One-Tap, prompts, and resolves the credential JWT
 * (or null if the user skips/dismisses). Entirely fail-safe — any error or missing SDK
 * resolves null so auto-login never breaks the page.
 *
 * Browser-only; verified via e2e / manual test on a registered origin (One-Tap requires
 * the page origin to be an authorized JavaScript origin for the client id).
 */

interface OneTapNotification {
    isSkippedMoment?: () => boolean
    isDismissedMoment?: () => boolean
    getDismissedReason?: () => string
}

interface GoogleIdentityServices {
    accounts: {
        id: {
            initialize: (config: {
                client_id: string
                auto_select: boolean
                use_fedcm_for_prompt: boolean
                callback: (response: { credential?: string }) => void
            }) => void
            prompt: (listener?: (notification: OneTapNotification) => void) => void
        }
    }
}

declare global {
    interface Window {
        google?: GoogleIdentityServices
    }
}

// Bound the prompt so a GSI moment that neither returns a credential nor reports a
// skip/dismiss cannot stall the auto-login chain.
const ONE_TAP_TIMEOUT_MS = 8000

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve()
            return
        }
        const el = document.createElement('script')
        el.src = src
        el.async = true
        el.onload = () => {
            resolve()
        }
        el.onerror = () => {
            reject(new Error(`Failed to load script: ${src}`))
        }
        document.head.appendChild(el)
    })
}

export async function promptGoogleOneTap(opts: {
    clientId: string
    scriptUrl: string
}): Promise<string | null> {
    if (typeof window === 'undefined') {
        return null
    }
    try {
        await loadScript(opts.scriptUrl)
        const gsi = window.google
        if (!gsi?.accounts?.id) {
            return null
        }

        return await new Promise<string | null>((resolve) => {
            let settled = false
            // Prefer the credential; never let a late skip/dismiss override a returned one,
            // and never resolve twice.
            const settle = (value: string | null) => {
                if (settled) {
                    return
                }
                settled = true
                clearTimeout(timer)
                resolve(value)
            }
            const timer = setTimeout(() => settle(null), ONE_TAP_TIMEOUT_MS)
            gsi.accounts.id.initialize({
                client_id: opts.clientId,
                auto_select: true,
                use_fedcm_for_prompt: true,
                callback: (response) => {
                    if (response?.credential) {
                        settle(response.credential)
                    }
                },
            })
            gsi.accounts.id.prompt((notification) => {
                const skipped = notification.isSkippedMoment?.() ?? false
                const dismissed = notification.isDismissedMoment?.() ?? false
                const gotCredential = notification.getDismissedReason?.() === 'credential_returned'
                // Resolve null on a skip/dismiss-without-credential so we never hang;
                // the success path resolves via the initialize() callback above.
                if (skipped || (dismissed && !gotCredential)) {
                    settle(null)
                }
            })
        })
    } catch {
        return null
    }
}
